import types from '../mutation-types';
import authAPI from '../../api/auth';

import { setUser, clearCookiesOnLogout } from '../utils/api';
import SessionStorage from 'shared/helpers/sessionStorage';
import { SESSION_STORAGE_KEYS } from 'dashboard/constants/sessionStorage';

const initialState = {
  currentUser: {
    id: null,
    account_id: null,
    accounts: [],
    email: null,
    name: null,
  },
  uiFlags: {
    isFetching: true,
    // Availability/accepts_chat fix — prevents click-spam and gives the
    // SidebarProfileMenuStatus a hook to disable its toggle while a
    // request is in flight. Defaults to false so initial render isn't
    // disabled.
    isUpdatingAvailability: false,
    isUpdatingAcceptsChat: false,
  },
};

// getters
export const getters = {
  isLoggedIn($state) {
    return !!$state.currentUser.id;
  },

  getCurrentUserID($state) {
    return $state.currentUser.id;
  },

  getUISettings($state) {
    return $state.currentUser.ui_settings || {};
  },

  getAuthUIFlags($state) {
    return $state.uiFlags;
  },

  getCurrentUserAvailability($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount.availability;
  },

  getCurrentUserAcceptsChat($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount.accepts_chat;
  },

  getCurrentAccountId(_, __, rootState) {
    if (rootState.route.params && rootState.route.params.accountId) {
      return Number(rootState.route.params.accountId);
    }
    return null;
  },

  getCurrentRole($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    const accountPermissions = currentAccount.permissions || [];
    if (accountPermissions.includes('administrator')) {
      return 'administrator';
    }

    return currentAccount.role;
  },

  getCurrentCustomRoleId($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount.custom_role_id;
  },

  getCurrentUser($state) {
    return $state.currentUser;
  },

  getMessageSignature($state) {
    const { message_signature: messageSignature } = $state.currentUser;

    return messageSignature || '';
  },

  getCurrentAccount($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount || {};
  },

  // Brand avatar for the account being viewed. The API only sends avatar_url on an
  // account when the feature is enabled, so this falls back to the global avatar
  // both when the feature is off and when no brand avatar has been uploaded.
  getCurrentAccountAvatarUrl($state, $getters) {
    return (
      $getters.getCurrentAccount.avatar_url || $state.currentUser.avatar_url
    );
  },

  getCurrentAccountHasCustomAvatar(_$state, $getters) {
    return !!$getters.getCurrentAccount.has_custom_avatar;
  },

  getUserAccounts($state) {
    const { accounts = [] } = $state.currentUser;
    return accounts;
  },
};

// actions
export const actions = {
  async validityCheck(context) {
    try {
      const response = await authAPI.validityCheck();
      const currentUser = response.data.payload.data;
      setUser(currentUser);
      context.commit(types.SET_CURRENT_USER, currentUser);
    } catch (error) {
      if (error?.response?.status === 401) {
        clearCookiesOnLogout('/app/login');
      }
    }
  },
  async setUser({ commit, dispatch }) {
    if (authAPI.hasAuthCookie()) {
      await dispatch('validityCheck');
    } else {
      commit(types.CLEAR_USER);
    }
    commit(types.SET_CURRENT_USER_UI_FLAGS, { isFetching: false });
  },
  logout({ commit }) {
    commit(types.CLEAR_USER);
  },

  updateProfile: async ({ commit }, params) => {
    // eslint-disable-next-line no-useless-catch
    try {
      const response = await authAPI.profileUpdate(params);
      commit(types.SET_CURRENT_USER, response.data);
    } catch (error) {
      throw error;
    }
  },

  updatePassword: async ({ commit }, params) => {
    // eslint-disable-next-line no-useless-catch
    try {
      const response = await authAPI.profilePasswordUpdate(params);
      commit(types.SET_CURRENT_USER, response.data);
    } catch (error) {
      throw error;
    }
  },

  deleteAvatar: async ({ commit }) => {
    try {
      const response = await authAPI.deleteAvatar();
      commit(types.SET_CURRENT_USER, response.data);
    } catch (error) {
      // Ignore error
    }
  },

  // Both re-throw, unlike deleteAvatar above: the brand avatar UI shows a toast on
  // failure, and silently swallowing a rejected upload would look like a success.
  updateAccountAvatar: async ({ commit }, { accountId, avatar }) => {
    const response = await authAPI.updateAccountAvatar({ accountId, avatar });
    commit(types.SET_CURRENT_USER, response.data);
  },

  deleteAccountAvatar: async ({ commit }, { accountId }) => {
    const response = await authAPI.deleteAccountAvatar({ accountId });
    commit(types.SET_CURRENT_USER, response.data);
  },

  updateUISettings: async ({ commit }, params) => {
    try {
      commit(types.SET_CURRENT_USER_UI_SETTINGS, params);

      const isImpersonating = SessionStorage.get(
        SESSION_STORAGE_KEYS.IMPERSONATION_USER
      );

      if (!isImpersonating) {
        const response = await authAPI.updateUISettings(params);
        commit(types.SET_CURRENT_USER, response.data);
      }
    } catch (error) {
      // Ignore error
    }
  },

  updateAvailability: async (
    { commit, dispatch, getters: _getters, state },
    params
  ) => {
    // Fix C — short-circuit if a previous request is still in-flight.
    // Without this, rapid clicks queue multiple POSTs whose responses arrive
    // out of order, and the last response (not the last click) wins.
    if (state.uiFlags.isUpdatingAvailability) return;

    const previousStatus = _getters.getCurrentUserAvailability;
    commit(types.SET_AUTH_UI_FLAG, { isUpdatingAvailability: true });

    try {
      // optimisticly update current status
      commit(types.SET_CURRENT_USER_AVAILABILITY, params.availability);
      const response = await authAPI.updateAvailability(params);
      const userData = response.data;
      const { id } = userData;
      commit(types.SET_CURRENT_USER, response.data);
      dispatch('agents/updateSingleAgentPresence', {
        id,
        availabilityStatus: params.availability,
      });
    } catch (error) {
      // revert back to previous status if update fails
      commit(types.SET_CURRENT_USER_AVAILABILITY, previousStatus);
      // Fix A/B — re-throw so the calling component can show a useAlert toast.
      // The catch-and-swallow pattern hid every failure (auth timeout, network
      // glitch, backend 500) from the user, which is what made the toggle
      // appear to "hang" without explanation.
      throw error;
    } finally {
      commit(types.SET_AUTH_UI_FLAG, { isUpdatingAvailability: false });
    }
  },

  updateAcceptsChat: async (
    { commit, getters: _getters, state },
    { accountId, acceptsChat }
  ) => {
    if (state.uiFlags.isUpdatingAcceptsChat) return;

    const previous = _getters.getCurrentUserAcceptsChat;
    commit(types.SET_AUTH_UI_FLAG, { isUpdatingAcceptsChat: true });

    try {
      commit(types.SET_CURRENT_USER_ACCEPTS_CHAT, acceptsChat);
      const response = await authAPI.updateAcceptsChat(accountId, acceptsChat);
      // Guard: only update user if we got valid account data back
      if (response.data?.id) {
        commit(types.SET_CURRENT_USER, response.data);
      }
    } catch (error) {
      commit(types.SET_CURRENT_USER_ACCEPTS_CHAT, previous);
      throw error;
    } finally {
      commit(types.SET_AUTH_UI_FLAG, { isUpdatingAcceptsChat: false });
    }
  },

  setCurrentUserAvailability({ commit, state: $state }, data) {
    if (data[$state.currentUser.id]) {
      commit(types.SET_CURRENT_USER_AVAILABILITY, data[$state.currentUser.id]);
    }
  },

  setActiveAccount: async (_, { accountId }) => {
    try {
      await authAPI.setActiveAccount({ accountId });
    } catch (error) {
      // Ignore error
    }
  },

  resetAccessToken: async ({ commit }) => {
    try {
      const response = await authAPI.resetAccessToken();
      commit(types.SET_CURRENT_USER, response.data);
      return true;
    } catch (error) {
      return false;
    }
  },

  resendConfirmation: async () => {
    try {
      await authAPI.resendConfirmation();
    } catch (error) {
      // Ignore error
    }
  },
};

// mutations
export const mutations = {
  [types.SET_CURRENT_USER_AVAILABILITY](_state, availability) {
    const accounts = _state.currentUser.accounts.map(account => {
      if (account.id === _state.currentUser.account_id) {
        return { ...account, availability, availability_status: availability };
      }
      return account;
    });
    _state.currentUser = {
      ..._state.currentUser,
      accounts,
    };
  },

  [types.SET_CURRENT_USER_ACCEPTS_CHAT](_state, acceptsChat) {
    const accounts = _state.currentUser.accounts.map(account => {
      if (account.id === _state.currentUser.account_id) {
        return { ...account, accepts_chat: acceptsChat };
      }
      return account;
    });

    _state.currentUser = {
      ..._state.currentUser,
      accounts,
    };
  },
  [types.CLEAR_USER](_state) {
    _state.currentUser = initialState.currentUser;
  },
  [types.SET_CURRENT_USER](_state, currentUser) {
    _state.currentUser = currentUser;
  },
  [types.SET_CURRENT_USER_UI_SETTINGS](_state, { uiSettings }) {
    _state.currentUser = {
      ..._state.currentUser,
      ui_settings: {
        ..._state.currentUser.ui_settings,
        ...uiSettings,
      },
    };
  },

  [types.SET_CURRENT_USER_UI_FLAGS](_state, { isFetching }) {
    // Preserve other uiFlags (isUpdatingAvailability/AcceptsChat) when only
    // isFetching changes; the existing call site only passes isFetching, so
    // the merge here keeps backward compat without forcing every caller to
    // pass the full flag set.
    _state.uiFlags = { ..._state.uiFlags, isFetching };
  },

  [types.SET_AUTH_UI_FLAG](_state, flags) {
    _state.uiFlags = { ..._state.uiFlags, ...flags };
  },
};

export default {
  state: initialState,
  getters,
  actions,
  mutations,
};
